
import React from 'react';
import { PublicNavbar } from '@/components/Layout/PublicNavbar';
import { Sparkles, Calendar, Users, Zap, Heart, CheckCircle } from 'lucide-react';

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-blue-50">
      <PublicNavbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-brand-primary/10 rounded-full">
                <Sparkles className="w-12 h-12 text-brand-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Acerca de <span className="brand-gradient-text">CoPlanner</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Transformamos la planificación de eventos en una experiencia simple, 
              organizada y sin complicaciones.
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Nuestra Misión
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  En CoPlanner, creemos que planificar un evento no debería ser una fuente de estrés. 
                  Nuestra misión es empoderar a organizadores de eventos con herramientas intuitivas 
                  que simplifican cada aspecto de la planificación.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Desde bodas íntimas hasta conferencias corporativas, nuestra plataforma te ayuda 
                  a crear experiencias memorables mientras mantienes todo organizado y bajo control.
                </p>
              </div>
              <div className="bg-gradient-to-br from-brand-primary/5 to-brand-secondary/10 p-8 rounded-2xl">
                <Heart className="w-16 h-16 text-brand-primary mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Eventos que Importan
                </h3>
                <p className="text-gray-600">
                  Cada evento tiene una historia única. Nosotros te proporcionamos las herramientas 
                  para que esa historia se cuente perfectamente.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                ¿Qué Hacemos?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                CoPlanner es tu compañero integral para la planificación de eventos, 
                ofreciendo todas las herramientas que necesitas en una sola plataforma.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="inline-flex p-3 bg-brand-primary/10 rounded-full mb-4">
                  <Calendar className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Gestión de Calendario
                </h3>
                <p className="text-gray-600">
                  Organiza fechas, horarios y cronogramas con nuestro sistema de calendario inteligente 
                  que se adapta a tus necesidades.
                </p>
              </div>

              <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="inline-flex p-3 bg-brand-primary/10 rounded-full mb-4">
                  <Users className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Gestión de Invitados
                </h3>
                <p className="text-gray-600">
                  Controla listas de invitados, confirmaciones de asistencia y comunicación 
                  directa a través de WhatsApp.
                </p>
              </div>

              <div className="text-center p-6 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="inline-flex p-3 bg-brand-primary/10 rounded-full mb-4">
                  <Zap className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Gestión de Proveedores
                </h3>
                <p className="text-gray-600">
                  Conecta con proveedores de confianza, gestiona cotizaciones y 
                  mantén toda la información centralizada.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nuestros Valores
              </h2>
              <p className="text-lg text-gray-600">
                Los principios que guían cada decisión que tomamos en CoPlanner.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-brand-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Simplicidad</h3>
                  <p className="text-gray-600">
                    Creemos que las herramientas poderosas deben ser fáciles de usar. 
                    Diseñamos cada función pensando en la simplicidad.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-brand-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Confiabilidad</h3>
                  <p className="text-gray-600">
                    Tu evento es importante para ti, y es importante para nosotros. 
                    Construimos tecnología en la que puedes confiar.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-brand-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Innovación</h3>
                  <p className="text-gray-600">
                    Constantemente evolucionamos nuestras herramientas para mantenernos 
                    a la vanguardia de las necesidades de nuestros usuarios.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-brand-primary to-brand-primary-hover">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              ¿Listo para Planificar tu Próximo Evento?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Únete a miles de organizadores que ya confían en CoPlanner.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/signup"
                className="bg-white text-brand-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors shadow-lg"
              >
                Empieza Gratis
              </a>
              <a
                href="/precios"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-brand-primary transition-colors"
              >
                Ver Precios
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default About;
